import React, { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import getConfig from 'next/config'
import Head from 'next/head'
import NdsThemeProvider from '@ntuctech/devex-tangram/Theme/NdsThemeProvider'
import ButtonContained from '@ntuctech/devex-tangram/Button/ButtonContained'
import ButtonText from '@ntuctech/devex-tangram/Button/ButtonText'
import Modal from '@ntuctech/devex-tangram/Modal'
import Router from 'next/router'
import ModalContent from '@ntuctech/devex-tangram/ModalContent'
import Grid from '@ntuctech/devex-tangram/Grid'
import { getAppUrlLink } from '../../utils/Utils'
import Image from '../../components/Image/Image'
import EverydaySaveBody from './EverydaySaveBody'
import EverydaySaveHeader from './EverydaySaveHeader'
import EveryDaySaveCard from './EveryDaySaveCard'
import Graph from '../../static/icons/Graph.svg'
import FairpriceIcon from '../../static/icons/icon-72x72.png'
import { get } from '../../lib/fetch'
import { SCREEN_SIZE } from '../../lib/Media'
import useMediaQuery from '../../components/DeliveryOrderDetail/hooks/useMediaQuery'
import DoanloadAppleApp from '../../static/icons/download_apple.svg'
import DownloadGoogleApp from '../../static/icons/download_google.svg'
import { SPLIT_FEATURES } from '../../utils/constants'
import { getConfigFor } from '../../utils/configService'
import {
  EventTrackingConsumer,
  EventTrackingContext,
} from '../../components/EventTrackingProvider/EventTrackingProvider'
import { GTM_ENV_VAR as GEV } from '../../lib/eventTrackerFunction'
import { AMP_EVENT_NAME } from '../../lib/amplitude'
import {
  eventsDataset,
  TRACKER_CONSTANTS,
} from '../../utils/constants/everydaySaveTracker'

const {
  publicRuntimeConfig: { STRUDEL_API },
} = getConfig()

const MainContainer = styled.div`
  margin-bottom: 3.5rem;
`

const DownloadApp = styled.div`
  padding: 0.5rem 0;
  gap: 0.5rem;
  position: fixed;
  width: 100%;
  height: 3.5rem;
  bottom: 0;
  background: #0b2e65;
  img {
    border-radius: 0.5rem;
    height: 2.5rem;
    width: 2.5rem;
  }
`

const GridNoPadding = styled(Grid)`
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledLabel = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #ffffff;
  padding-top: 0.25rem;
  margin: 0 0.5rem;
  ${SCREEN_SIZE.Below.Mobile} {
    width: 9.313rem;
  }
`

const StyledButton = styled.button`
  cursor: pointer;
  width: 9.375rem;
  height: 2.25rem;
  background: #ffffff;
  border-radius: 0.25rem;
  color: #1454b8;
  font-weight: 700;
  line-height: 1.25rem;
  border: 0;
  &:hover {
    background-color: rgb(232, 240, 253);
  }
  &:active {
    box-shadow: rgb(163, 194, 245) 0rem 0rem 0rem 0.1875rem !important;
  }
`

const StyledContentLabel = styled.div`
  min-width: 18.5rem;
  height: 1.75rem;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.75rem;
`

const StyledContentSubLabel = styled.div`
  min-width: 18.5rem;
  height: 3rem;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5rem;
`

const StyledButtonContainer = styled.div`
  margin-top: 1rem;
  width: 100%;
  button {
    width: 100%;
    padding: 0;
    margin: 0;
  }
  & > button:last-child {
    margin: 0;
  }
`

const ModalImageContainer = styled.div`
  flex: none;
  order: 0;
  flex-grow: 0;
  margin-bottom: 1rem;
  width: 100%;
  height: 100%;
`

const BodyContainer = styled.div`
  border-radius: 0.25rem;
  ${SCREEN_SIZE.From.Tablet} {
    margin: 0 2rem 0;
    background-color: #ffffff;
  }
  ${SCREEN_SIZE.From.Desktop} {
    margin: 0 2.5rem 0;
    background-color: #ffffff;
  }
`

const StyledDesktopButtonContainer = styled.div`
  margin-top: 1rem;
  float: right;
  > button {
    margin: 0;
    padding: 0;
  }
`

const ImageAppContainer = styled.image`
  float: left;
  margin-top: 1rem;
  display: flex;
  > div {
    margin-right: 0.5rem;
  }
`

const StyledImageDownload = styled(Image)`
  margin-right: 0.5rem;
  width: 7.5rem !important;
  height: 2.5rem !important;
  cursor: pointer;
`

const StyledAndroidImageDownload = styled(Image)`
  width: 8.313rem !important;
  height: 2.5rem !important;
  cursor: pointer;
`

export const GA = { eventsDataset, TRACKER_CONSTANTS }

const handleDownloadAppClick = (src, trackEvent, contents) => {
  trackEvent({
    [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.SEC_DOWNLOAD_CLICK,
    [GEV.AMPLITUDE_VERTICAL]: 'rewards',
    [GEV.AMPLITUDE_EVENT_TYPE]: AMP_EVENT_NAME.VOUCHER_EVENT_PROPERTY,
    [GEV.AMPLITUDE_CTA_LOCATION]: `rewards | ${contents?.pageTitle}`,
    [GEV.AMPLITUDE_CALL_TO_ACTION_SUB_LOCATION]: 'Bottom Banner',
    [GEV.SEC_LANDING_PAGE]: src,
  })
  window.open(getAppUrlLink())
}

const RenderModal = (open, isMobile, track, contents, setOpen) => (
  <NdsThemeProvider>
    <Modal open={open}>
      <ModalContent>
        <ModalImageContainer>
          <Image width="100%" height="100%" src={Graph} />
        </ModalImageContainer>
        <StyledContentLabel>
          One step closer to saving every day!
        </StyledContentLabel>
        <StyledContentSubLabel>
          Download the FairPrice app to access all 400 discount vouchers.
        </StyledContentSubLabel>
        {!isMobile && (
          <ImageAppContainer>
            <StyledImageDownload
              data-testid="popup-apple"
              src={DoanloadAppleApp}
              onClick={() =>
                handleDownloadAppClick('Apple App Store', track, contents)
              }
            />
            <StyledImageDownload
              data-testid="popup-google"
              src={DownloadGoogleApp}
              onClick={() =>
                handleDownloadAppClick('Google Play store', track, contents)
              }
            />
          </ImageAppContainer>
        )}
      </ModalContent>
      {isMobile ? (
        <>
          <StyledButtonContainer>
            <ButtonContained
              data-testid="download-mobile-btn"
              onClick={() => {
                window.open(getAppUrlLink())
                GA.track(
                  GA.eventsDataset[GA.TRACKER_CONSTANTS.ON_APP_DOWNLOAD_CLICK]
                )
                handleDownloadAppClick('', track, contents)
                setOpen(false)
              }}
            >
              Download app
            </ButtonContained>
          </StyledButtonContainer>
          <StyledButtonContainer>
            <ButtonText
              data-testid="close-modal"
              onClick={() => setOpen(false)}
              color="grey"
            >
              Close
            </ButtonText>
          </StyledButtonContainer>
        </>
      ) : (
        <StyledDesktopButtonContainer>
          <ButtonContained onClick={() => setOpen(false)}>
            Close
          </ButtonContained>
        </StyledDesktopButtonContainer>
      )}
    </Modal>
  </NdsThemeProvider>
)

const EverydaySaveWrp = () => {
  const [open, setOpen] = useState(false)
  const [campaigns, setCampaigns] = useState([])
  const [filterList, setFilterList] = useState({})
  const [contents, setContents] = useState({
    pageTitle: 'Save Every Day 2025',
    pageSubTitle: 'In-store',
    pageDescription:
      'Vouchers are applicable online and in-store with Scan & Go or payment via the FairPrice app.',
    campaignPeriod: '',
    pageImgUrl: '',
  })
  const { track } = useContext(EventTrackingContext)
  GA.track = track

  useEffect(() => {
    const fetchCampaigns = async () => {
      const response = await get(
        `${STRUDEL_API}/rms/me/campaigns/sec-2025/vouchers?withSavedStatus=false`
      )
      const {
        pageTitle,
        pageSubTitle,
        pageDescription,
        campaignPeriod,
        bannerImageM,
        bannerImageS,
        bannerImageXs,
        filter,
        howToRedeem,
        howToRedeemUrl,
      } = response
      setCampaigns(response.list)
      setContents({
        pageTitle,
        pageSubTitle,
        pageDescription,
        campaignPeriod,
        bannerImageM,
        bannerImageS,
        bannerImageXs,
        howToRedeem,
        howToRedeemUrl,
      })
      setFilterList(filter)
    }

    if (campaigns.length === 0) {
      fetchCampaigns()
    }
  }, [])

  const isMobile = useMediaQuery(SCREEN_SIZE.Below.Tablet)
  const isTablet = useMediaQuery(SCREEN_SIZE.Only.Tablet)

  const { isEnabled: isSECLandingPageEnable } = getConfigFor(
    SPLIT_FEATURES.SEC_LANDING_PAGE
  )

  React.useEffect(() => {
    if (!isSECLandingPageEnable) {
      Router.push('accounts?tab=vouchers')
    }
  }, [isSECLandingPageEnable])

  React.useEffect(() => {
    GA.track(GA.eventsDataset[GA.TRACKER_CONSTANTS.LANDED_SEC_CAMPAIGN_PAGE])
  }, [])

  React.useEffect(() => {
    open &&
      GA.track(GA.eventsDataset[GA.TRACKER_CONSTANTS.OPEN_SEC_DOWNLOAD_POPUP])
    track({
      [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.SEC_PROMPT_POPUP_VIEWED,
      [GEV.AMPLITUDE_VERTICAL]: 'rewards',
      [GEV.AMPLITUDE_CTA_LOCATION]: `rewards | ${contents?.pageTitle}`,
      [GEV.AMPLITUDE_CTA_SUBLOCATION_SECTION_TYPE]: 'dynamic',
      'source': '',
    })
  }, [open])

  return (
    <>
      <Head>
        <title>{`${contents?.pageTitle}`}</title>
        <meta
          name="description"
          key="meta_description"
          content={contents?.pageDescription}
        />
      </Head>
      <MainContainer>
        {isSECLandingPageEnable ? (
          <>
            <EverydaySaveHeader
              isTablet={isTablet}
              isMobile={isMobile}
              logo={
                isMobile
                  ? contents.bannerImageXs
                  : isTablet
                  ? contents.bannerImageS
                  : contents.bannerImageM
              }
            />
            <BodyContainer>
              <EverydaySaveBody contents={contents} filterList={filterList} />
              <EveryDaySaveCard
                track={track}
                isMobile={isMobile}
                isTablet={isTablet}
                setOpen={setOpen}
                cardData={campaigns}
                GA={GA}
              />
            </BodyContainer>

            <DownloadApp>
              <GridNoPadding container>
                <Image alt="" src={FairpriceIcon} />
                <StyledLabel>
                  Download app to use these vouchers now!
                </StyledLabel>
                {isTablet || isMobile ? (
                  <StyledButton
                    data-testid="download-btn-ftr"
                    onClick={() => handleDownloadAppClick('', track, contents)}
                  >
                    Download app
                  </StyledButton>
                ) : (
                  <>
                    <StyledImageDownload
                      data-testid="download-apple"
                      src={DoanloadAppleApp}
                      onClick={() =>
                        handleDownloadAppClick(
                          'Apple App Store',
                          track,
                          contents
                        )
                      }
                    />
                    <StyledAndroidImageDownload
                      id="android"
                      data-testid="download-android"
                      src={DownloadGoogleApp}
                      onClick={() =>
                        handleDownloadAppClick(
                          'Google Play store',
                          track,
                          contents
                        )
                      }
                    />
                  </>
                )}
              </GridNoPadding>
            </DownloadApp>
          </>
        ) : null}
        {RenderModal(open, isMobile, track, contents, setOpen)}
      </MainContainer>
    </>
  )
}

const EverydaySave = props => (
  <EventTrackingConsumer>
    {({ track }) => <EverydaySaveWrp {...props} track={track} />}
  </EventTrackingConsumer>
)

export default EverydaySave
